import { videoEditor } from '@mi-front/api-clients';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import { Fragment } from '../../../../types';

interface CloneVideoEditionProjectDialogProps {
  open: boolean;
  onClose: () => void;
  fragment: Fragment;
  sourceProject: videoEditor.Project;
}

/**
 * Dialog to clone an existing video editor project with a new name.
 */
export function CloneVideoEditionProjectDialog({
  open,
  onClose,
  fragment,
  sourceProject,
}: CloneVideoEditionProjectDialogProps) {
  const { $t } = useIntl();
  const queryClient = useQueryClient();
  const cloneMutation = videoEditor.useProjectsClone({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: videoEditor.projectsListQueryKey({
            sourceType: 'FRAGMENT',
            sourceId: fragment.id,
          }),
        });
        onClose();
      },
    },
  });

  const defaultName = `${sourceProject.name} (copia)`;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const input = form.elements.namedItem('name') as HTMLInputElement | null;
    const name = input?.value?.trim() || defaultName;
    cloneMutation.mutate({
      id: sourceProject.id,
      data: { name },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <FormattedMessage
          id="fragments.videoEditions.cloneDialog.title"
          defaultMessage="Clonar proyecto"
        />
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            name="name"
            label={$t({
              id: 'fragments.videoEditions.cloneDialog.nameLabel',
              defaultMessage: 'Nombre',
            })}
            placeholder={defaultName}
            defaultValue={defaultName}
            fullWidth
            required
            autoFocus
            error={!!cloneMutation.error}
            helperText={cloneMutation.error ? (cloneMutation.error as Error).message : undefined}
            disabled={cloneMutation.isPending}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={cloneMutation.isPending}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancelar" />
          </Button>
          <Button type="submit" variant="contained" disabled={cloneMutation.isPending}>
            <FormattedMessage
              id="fragments.videoEditions.cloneDialog.submit"
              defaultMessage="Clonar"
            />
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
